/* Copyright 2017 VMware, Inc. All rights reserved. -- VMware Confidential */
namespace dvs_ui {

   export class DvsPortsSerivce {

      private i18n: Function;

      private readonly EMPTY_PROPERTY_VALUE: string;

      static $inject = [
         "i18nService",
         "columnRenderersRegistry",
         "networkUtil",
         "vxPropertyViewService"
      ];

      constructor(private i18nService: any,
                  private columnRenderersRegistry: any,
                  private networkUtil: any,
                  private vxPropertyViewService: any) {
         this.i18n = this.i18nService.getString;
         this.EMPTY_PROPERTY_VALUE = this.i18n("DvsUi", "PortList.noData");
      }

      public getColumnDefinitions (): any[] {
         const columnDefs: any[] = [
            this.newNumericTextColumn("PortList.keyHeaderText", "portId", "50px"),
            this.newTextColumn("PortList.nameHeaderText", "nameText"),
            this.newTextColumn("PortList.connecteeHeaderText", "connecteeName"),
            this.newTextColumn("PortList.macHeaderText", "macText"),
            this.newTextColumn("PortList.pgHeaderText", "portgroupText"),
            {
               displayName: this.i18n("DvsUi", "PortList.stateHeaderText"),
               field: "stateText",
               width: "90px",
               template: (dataItem: any) => {
                  return this.columnRenderersRegistry.getColumnRenderer("icon-text")(
                        ["stateIcon", "stateText"], dataItem, false);
               }
            },
            this.newTextColumn("PortList.vlanIdHeaderText", "vlanText")
         ];

         return columnDefs;
      }

      private newTextColumn(columnNameKey: string, dataFieldName: string,
            columnWidth: string = "90px"): any {
         return {
            displayName: this.i18n("DvsUi", columnNameKey),
            field: dataFieldName,
            width: columnWidth,
            template: (dataItem: any) => {
               return this.columnRenderersRegistry.getColumnRenderer("text")(
                     [dataFieldName], dataItem, false);
            }
         };
      }

      private newNumericTextColumn(columnNameKey: string, dataFieldName: string,
            columnWidth: string = "90px"): any {
         return {
            displayName: this.i18n("DvsUi", columnNameKey),
            field: dataFieldName,
            width: columnWidth,
            template: (dataItem: any) => {
               return dataItem[dataFieldName]
                     ? this.columnRenderersRegistry.getColumnRenderer("text")(
                           [dataFieldName], dataItem, false)
                     : this.EMPTY_PROPERTY_VALUE;
            },
            sortable: (item1: any, item2: any): any => {
               return this.networkUtil.compareNumericAndNonNumericValues(
                     item1, item2, dataFieldName);
            }
         };
      }

      public formatDisplayStrings(ports: Array<any>): Array<any> {
         ports.forEach((port: any) => {
            //format Connectee column text
            if (!port.connectedEntity) {
               port.connecteeName = this.EMPTY_PROPERTY_VALUE;
            } else if (port.connectedEntityType === DvsPortsConstants.VM_VNIC) {
               port.connecteeName = port.connectedEntity;
            } else {
               port.connecteeName = `${port.host} - ${port.connectedEntity}`;
            }

            //format State column text
            switch (port.state) {
               case "LINK_UP": {
                  port.stateText = this.i18n("DvsUi", "PortState.linkUp");
                  port.stateIcon = "network-lib-ui-icon-dvsPortConnectedLf";
                  break;
               }
               case "LINK_DOWN": {
                  port.stateText = this.i18n("DvsUi", "PortState.linkDown");
                  port.stateIcon = "network-lib-ui-icon-dvsPortNotconnectedLf";
                  break;
               }
               case "BLOCKED": {
                  port.stateText = this.i18n("DvsUi", "PortState.blocked");
                  port.stateIcon = "network-lib-ui-icon-dvsPortBlockedLf";
                  break;
               }
               default: {
                  port.stateText = this.EMPTY_PROPERTY_VALUE;
                  port.stateIcon = "";
               }
            }

            this.formatTextItem(port, "name");
            this.formatTextItem(port, "mac");
            this.formatTextItem(port, "portgroup");
            this.formatTextItem(port, "vlan");
         });

         return ports;
      }

      private formatTextItem(port: any, dataField: string): void {
         port[dataField + "Text"] = port[dataField]
               ? port[dataField]
               : this.EMPTY_PROPERTY_VALUE;
      }

      public buildDvsPortNetworkConnectionPropertiesView(dvsPortDetailsData: any): any {
         if (dvsPortDetailsData) {
            let builder = this.vxPropertyViewService.createPropertyViewBuilder();
            this.buildDvsPortNetworkConnectionCategory(builder, dvsPortDetailsData);

            return builder.build();
         }
      }

      public assignLiveRefreshProperties(isPortgroupContext: boolean): string[] {
         return isPortgroupContext ? DvsPortsConstants.LIVE_REFRESH_PROPERTIES : [];
      }

      private buildDvsPortNetworkConnectionCategory(
            builder: any, dvsPortDetailsData: any) {
         let generalCategory: any = builder.category("general");
         let generalSection: any = generalCategory.section("generalSection");

         generalSection.property(this.i18n(
               "DvsUi", "PortDetails.portId"), dvsPortDetailsData.portId);

         if (dvsPortDetailsData.name) {
            generalSection.property(this.i18n(
                  "DvsUi", "PortDetails.name"), dvsPortDetailsData.name);
         }

         if (dvsPortDetailsData.description) {
            generalSection.property(this.i18n(
                  "DvsUi", "PortDetails.description"), dvsPortDetailsData.description);
         }

         if (dvsPortDetailsData.lagName) {
            generalSection.property(this.i18n(
                  "DvsUi", "PortDetails.lagName"), dvsPortDetailsData.lagName);
         }

         if (dvsPortDetailsData.connectedEntity) {
            let connecteeObj: any = {
               icon: "",
               text: this.EMPTY_PROPERTY_VALUE
            };

            switch (dvsPortDetailsData.connectedEntityType) {
               case DvsPortsConstants.VMK_VNIC: {
                  connecteeObj.text =
                        `${dvsPortDetailsData.host} - ${dvsPortDetailsData.connectedEntity}`;
                  connecteeObj.icon = "network-lib-ui-icon-virtualAdapter";
                  break;
               }
               case DvsPortsConstants.VM_VNIC: {
                  connecteeObj.text = `${dvsPortDetailsData.connectedEntity}`;
                  connecteeObj.icon = "vsphere-icon-vm";
                  break;
               }
               case DvsPortsConstants.PNIC: {
                  connecteeObj.text =
                        `${dvsPortDetailsData.host} - ${dvsPortDetailsData.connectedEntity}`;
                  connecteeObj.icon = "network-lib-ui-icon-physicalAdapter";
                  break;
               }
            }

            this.addPropertyValueWithIcon(
                  generalSection,
                  this.i18n("DvsUi", "PortDetails.connectee"),
                  connecteeObj);
         }

         let stateObj: any = {
            icon: "",
            text: this.EMPTY_PROPERTY_VALUE
         };

         switch (dvsPortDetailsData.state) {
            case "LINK_UP": {
               stateObj.text = this.i18n("DvsUi", "PortState.linkUp");
               stateObj.icon = "network-lib-ui-icon-dvsPortConnectedLf";
               break;
            }
            case "LINK_DOWN": {
               stateObj.text = this.i18n("DvsUi", "PortState.linkDown");
               stateObj.icon = "network-lib-ui-icon-dvsPortNotconnectedLf";
               break;
            }
            case "BLOCKED": {
               stateObj.text = this.i18n("DvsUi", "PortState.blocked");
               stateObj.icon = "network-lib-ui-icon-dvsPortBlockedLf";
               break;
            }
         }

         this.addPropertyValueWithIcon(
               generalSection,
               this.i18n("DvsUi", "PortDetails.state"),
               stateObj);

         if (dvsPortDetailsData.mac) {
            generalSection.property(this.i18n("DvsUi", "PortDetails.mac"),
                  dvsPortDetailsData.mac);
         }

      }

      private addPropertyValueWithIcon(section: any, propertyName: String, propValue: any) {
         return section.propertyBuilder()
               .keyBuilder().text(propertyName)
               .exit()
               .valueBuilder()
               .icon(propValue.icon)
               .text(propValue.text)
               .exit()
               .exit();
      }
   }

   angular.module("com.vmware.vsphere.client.dvs")
         .service("dvsPortsService", DvsPortsSerivce);
}
